import Card from "components/Card";
import Page from "components/Page";
import Text from "components/Text";
import React, { useEffect, useState } from "react";
import DeleteIcon from "../../../../assets/images/delete-icon.svg";
// import EditIcon from "../../../../assets/images/edit-icon.svg";
import styles from "../../accountpreferences.module.scss";
import { MoreTagsFunction } from "accountOwner/pages/AddEvent/addEvents.functions";
import constants from "../../../../constants";
import {
  deleteDocument,
  updateDocument,
  viewDocumentDetail,
} from "repositories/ai-assistant-repository";
import { formatLocalDateTime, handleError } from "utils";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import { withTranslation } from "react-i18next";
import RolePopup from "accountOwner/pages/AddEvent/components/RolePopup";
import moment from "moment";
import Loader from "components/Loader";
import UploadModal from "components/CustomModal/UploadModal";
import useUploadService from "hooks/useUploadService";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const containerName = "ai-assistant";
function ViewManageAiDocuments({ t, location, history }) {
  const session = location?.state;
  const { upload: uploadImage } = useUploadService();
  const { selectedId, getIdToCheckOwnerChange } = useAccountOwnerCommon();
  useEffect(() => {
    if (getIdToCheckOwnerChange()) {
      history.goBack();
    }
  }, [selectedId]);
  const [documentDetail, setDocumentDetail] = useState({});
  const [officePopupOpen, setOfficePopupOpen] = useState(false);
  const [rolePopupOpen, setRolePopupOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });

  const goToPreviousScreen = () => {
    history.push(
      session?.isFromSuperAdmin
        ? constants.routes.aiAssistant.manageDocuments
        : constants.routes.aiAssistant.manageAiDocuments,
    );
  };

  const handlePopupToggle = (event, setter) => {
    event.stopPropagation();
    setInvitePopupPosition({
      top: 40,
      left: 40,
    });
    setter((prev) => !prev);
  };

  useEffect(() => {
    if (selectedId && session?.ownerId && selectedId !== session?.ownerId) {
      goToPreviousScreen();
    }
  }, [selectedId]);

  useEffect(() => {
    fetchDetail();
  }, []);

  const fetchDetail = async () => {
    try {
      setShowLoader(true);
      const response = await viewDocumentDetail(session?.documentId);
      setDocumentDetail(response);
    } catch (error) {
      handleError(error);
    } finally {
      setShowLoader(false);
    }
  };

  const uploadFilesToAPI = async (fileObjects) => {
    let uploadedDocuments = [];

    for (let i = 0; i < fileObjects.length; i++) {
      let fileObj = fileObjects[i];

      if (fileObj.file && !fileObj.isUploaded) {
        try {
          const [err, blobData] = await uploadImage(
            fileObj.file,
            containerName,
          );

          if (err) {
            throw new Error(err);
          }

          uploadedDocuments.push({
            documentPath: `${containerName}/${blobData.blobName}`,
            documentName: fileObj.documentName,
          });

          fileObj.documentPath = blobData.blobUrl;
          fileObj.isUploaded = true;
        } catch (error) {
          throw error;
        }
      } else if (fileObj.documentPath && fileObj.isUploaded) {
        uploadedDocuments.push({
          documentPath: fileObj.documentPath,
          documentName: fileObj.documentName,
        });
      }
    }

    return uploadedDocuments;
  };

  const confirmDeleteDocument = async () => {
    setShowLoader(true);
    try {
      const resp = await deleteDocument(documentDetail?.id);
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.success(resp?.message);
      goToPreviousScreen();
    } catch (e) {
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.error(e.message);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      if (!documentDetail?.documentPath) {
        toast.error(t("aiAssistant.documentPathNotAvailable"));
        return;
      }

      setShowLoader(true);

      // Fetch the file as a blob
      const response = await fetch(documentDetail.documentPath);
      if (!response.ok) {
        throw new Error(t("aiAssistant.failedToFetchDocument"));
      }

      const blob = await response.blob();

      // Create blob URL and download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        documentDetail?.documentName ||
        documentDetail.documentPath.split("/").pop();

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(url);

      setShowLoader(false);
      toast.success(t("aiAssistant.downloadedSuccessfully"));
    } catch (error) {
      setShowLoader(false);
      toast.error(t("aiAssistant.failedToDownloadDocument"));
      handleError(error);
    }
  };

  const handleSave = async (filesWithData) => {
    try {
      setShowLoader(true);

      // Check for duplicates
      const existingDocuments = session?.documentList || [];
      const duplicateFiles = [];

      filesWithData.forEach((fileData) => {
        const isDuplicate = existingDocuments.some(
          (existingDoc) =>
            existingDoc.documentName.toLowerCase() ===
            fileData.documentName.toLowerCase(),
        );
        if (isDuplicate) {
          duplicateFiles.push(fileData.documentName);
        }
      });

      if (duplicateFiles.length > 0) {
        setShowLoader(false);
        toast.error(`
          Duplicate files found: ${duplicateFiles.join(
            ", ",
          )}. Please remove duplicates and try again.
        `);
        return;
      }

      const uploadedFilesWithPaths = await uploadFilesToAPI(filesWithData);

      const aiAssistantProjectDocuments = filesWithData.map(
        (fileData, index) => ({
          documentPath:
            uploadedFilesWithPaths[index]?.documentPath ||
            fileData.documentPath,
          DocumentName: fileData.documentName,
          Description: fileData.Description,
          Version: fileData.Version,
          Category: fileData.Category,
          ExpirationDate: fileData.ExpirationDate,
          IsForAllOffice: fileData.IsForAllOffice,
          IsForAllDesignation: fileData.IsForAllDesignation,
          Id: documentDetail?.id,
          ...(fileData.OfficeIds && { OfficeIds: fileData.OfficeIds }),
          ...(fileData.DesignationIds && {
            DesignationIds: fileData.DesignationIds,
          }),
        }),
      );

      let payload;

      const file = aiAssistantProjectDocuments[0];
      payload = {
        OwnerId: session?.ownerId,
        ...file,
      };
      const result = await updateDocument(payload);
      toast.success(result?.message);

      fetchDetail();
      setShowLoader(false);
      setIsUploadModalOpen(false);
    } catch (error) {
      setShowLoader(false);
    }
  };

  return (
    <Page title={"Document Details"} onBack={goToPreviousScreen}>
      {showLoader && <Loader />}
      <Card className={styles.manage_ai_card}>
        <div className="d-flex justify-content-between">
          <Text size="20px" weight="500" color="#2D3245">
            {documentDetail?.documentName}
          </Text>
          <div className={styles.image_container}>
            <img
              src={DeleteIcon}
              alt="DeleteIcon"
              onClick={() => setConfirmDeleteModal(true)}
            />
            {/* Intentionally commented out */}
            {/* <img
              src={EditIcon}
              alt="EditIcon"
              onClick={() => setIsUploadModalOpen(true)}
            /> */}
          </div>
        </div>

        <div className={styles.document_details_container}>
          <div>
            <Text size="12px" weight="400" color="#6F7788">
              {t("aiAssistant.documentName")}
            </Text>
            <Text size="14px" weight="600" color="#2D3245">
              {documentDetail?.documentName}
            </Text>
          </div>
          <div
            className={`c-form-control ${styles.link_btn_style}`}
            style={{ position: "relative" }}
          >
            <Text size="12px" weight="400" color="#6F7788">
              {t("taskManagement.offices")}
            </Text>
            <div style={{ position: "relative" }}>
              {documentDetail?.offices?.length > 0 ? (
                <MoreTagsFunction
                  value={documentDetail?.offices}
                  clickHandler={(e) => handlePopupToggle(e, setOfficePopupOpen)}
                  mapFn={(item) => item?.name || ""}
                  lastIndex={3}
                />
              ) : (
                <Text size="14px" weight="600" color="#2D3245">
                  {t("superAdmin.allOffices")}
                </Text>
              )}
              {officePopupOpen && (
                <InviteesPopup
                  position={invitePopupPosition}
                  handleInvitePopupToggle={(e) =>
                    handlePopupToggle(e, setOfficePopupOpen)
                  }
                  list={documentDetail?.offices}
                  showRoleAndOffice={false}
                  isForEvent={true}
                  onlyOfffice
                  isfromBusySlot
                />
              )}
            </div>
          </div>
          <div
            className={`c-form-control ${styles.link_btn_style}`}
            style={{ position: "relative" }}
          >
            <Text size="12px" weight="400" color="#6F7788">
              {t("roles")}
            </Text>
            <div style={{ position: "relative" }}>
              {documentDetail?.designations?.length > 0 ? (
                <MoreTagsFunction
                  value={documentDetail?.designations}
                  clickHandler={(e) => handlePopupToggle(e, setRolePopupOpen)}
                  mapFn={(item) => item?.name || ""}
                  lastIndex={3}
                />
              ) : (
                <Text size="14px" weight="600" color="#2D3245">
                  {t("dashBoard.allRoles")}
                </Text>
              )}
              {rolePopupOpen && (
                <RolePopup
                  handlePopupToggle={(e) =>
                    handlePopupToggle(e, setRolePopupOpen)
                  }
                  position={invitePopupPosition}
                  list={documentDetail?.designations}
                  isForEvent={true}
                />
              )}
            </div>
          </div>

          <div className={styles.two_col_grid}>
            <div>
              <Text size="12px" weight="400" color="#6F7788">
                {t("form.fields.versionNumber")}
              </Text>
              <Text size="14px" weight="600" color="#2D3245">
                {documentDetail?.version}
              </Text>
            </div>
            <div>
              <Text size="12px" weight="400" color="#6F7788">
                {t("aiAssistant.fileType")}
              </Text>
              <Text size="14px" weight="600" color="#2D3245">
                {documentDetail?.documentType}
              </Text>
            </div>
          </div>

          <div>
            <Text size="12px" weight="400" color="#6F7788">
              {t("superAdmin.category")}
            </Text>
            <Text size="14px" weight="600" color="#2D3245">
              {documentDetail?.categoryName}
            </Text>
          </div>

          <div className={styles.two_col_grid}>
            <div>
              <Text size="12px" weight="400" color="#6F7788">
                {t("aiAssistant.uploadedOn")}
              </Text>
              <Text size="14px" weight="600" color="#2D3245">
                {formatLocalDateTime(documentDetail?.createdAt, "MMM Do, YYYY")}
              </Text>
            </div>
            <div>
              <Text size="12px" weight="400" color="#6F7788">
                {t("aiAssistant.expirationDate")}
              </Text>
              <Text size="14px" weight="600" color="#2D3245">
                {moment(documentDetail?.expirationDate).format("MMM Do, YYYY")}
              </Text>
            </div>
          </div>

          <div>
            <Text size="12px" weight="400" color="#6F7788">
              {t("aiAssistant.description")}
            </Text>
            <Text size="14px" weight="600" color="#2D3245">
              {documentDetail?.description}
            </Text>
          </div>

          <div className={`toggle_btn_wrapper ${styles.btn_border}`}>
            <Text size="12px" weight="400" color="#6F7788">
              {t("aiAssistant.manageDocuments.attachments")}
            </Text>
            <div className="d-flex justify-content-between">
              <Text size="14px" weight="600" color="#2D3245">
                {documentDetail?.documentPath?.split("/").pop()}
              </Text>
              <button
                className="link-btn border-0 bg-transparent"
                onClick={handleDownloadDocument}
              >
                {t("officePolicyManagement.downloadFile")}
              </button>
            </div>
          </div>
        </div>
      </Card>
      {isUploadModalOpen && (
        <UploadModal
          isOpen={isUploadModalOpen}
          setIsOpen={() => setIsUploadModalOpen(!isUploadModalOpen)}
          onClose={() => setIsUploadModalOpen(false)}
          onSave={handleSave}
          existingDocuments={session?.documentList || []}
          staffRoleData={session?.staffRoleData}
          officeData={session?.officeData?.data || []}
          categoryList={session?.categoryList || []}
          isEditMode={true}
          fromSuperAdmin={session?.isFromSuperAdmin}
        />
      )}
      {confirmDeleteModal && (
        <DeleteConfirmationModal
          closeDeleteModal={() => setConfirmDeleteModal(false)}
          confirmDelete={confirmDeleteModal}
          confirmDeleteMessage={confirmDeleteDocument}
          deleteMessage={t("aiAssistant.deleteMessage")}
          title={t("aiAssistant.deleteModalTitle")}
          btnText={t("delete")}
        />
      )}
    </Page>
  );
}

export default withTranslation()(ViewManageAiDocuments);
